import type { ApplicationStatusResponse } from "@credit-acceptance/step6-bff-client";
import { queryOptions, useQuery } from "@tanstack/react-query";

import { applicationStatusApi } from "../../../api/client";
import { applicationStatusKeys } from "./applicationStatusKeys";

/**
 * Shared queryOptions for a deal's application status + phase (key + queryFn).
 * Backed by the generated ApplicationStatusApi.getApplicationStatus, which
 * proxies the Credit Application Service and returns human-readable
 * `statusDescription` / `phaseDescription`. This is a separate read from the
 * DSC Workbench dashboard, so the Deal Intelligence Card can render immediately
 * from the dashboard and fill in status/phase once this resolves.
 *
 * Reusable by useQuery, prefetchQuery, ensureQueryData, getQueryData, and
 * setQueryData.
 * @param {string} approvalId - The deal / approval identifier.
 * @returns {ReturnType<typeof queryOptions>} Typed options object.
 */
export function applicationStatusQuery(approvalId: string) {
  return queryOptions({
    queryKey: applicationStatusKeys.detail({ approvalId }),
    queryFn: async ({ signal }): Promise<ApplicationStatusResponse> => {
      const response = await applicationStatusApi.getApplicationStatus(approvalId, { signal });
      return response.data;
    },
    staleTime: 30_000,
    enabled: !!approvalId,
  });
}

/**
 * Reads the application status + phase for a deal. Disabled (never fires) until
 * the approval id is present, so callers can render an empty state instead of
 * issuing a doomed `/applications//status` request.
 * @param {string} approvalId - The deal / approval identifier.
 * @returns {ReturnType<typeof useQuery<ApplicationStatusResponse>>} The TanStack Query result.
 */
export function useApplicationStatus(approvalId: string) {
  return useQuery(applicationStatusQuery(approvalId));
}
